import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRouteMatch } from 'react-router-dom';
import animateScrollTo from 'animated-scroll-to';

import { DISCOUNTS } from 'store/carInstance/constants';
import { onlyNumber } from 'utils/stringUtils';

import { BLOCK_MAIN } from '../../constants';

import { DEFAULT_DISABLED, DEFAULT_VALUE, FIELDS_NAME } from './BlockMain.constants';
import * as help from './BlockMain.helpers';

export const useBlockMainPresenter = ({
  allFetch,
  brands,
  brandsFetch,
  carInfoFetch,
  carInstance,
  disabled: disabledProp,
  disableFields: disableFieldsProp,
  hiddenFields,
  isInCredit,
  models,
  modelsFetch,
  onBlur,
  onChange,
  onChangeCarModel,
  onClickFullCalc,
  onClickIsInCredit,
  onPatch,
  onResetErrors,
  onScroll,
  onSetCarInstance,
  onSetYear,
  onValidate,
  patchIsNew,
  requiredFields,
  scrollTo,
  setImage,
  setMileage,
  showRestrictions,
  versions,
  versionsFetch,
}) => {
  const ref = useRef();
  const { path } = useRouteMatch();

  const [brand, setBrand] = useState(DEFAULT_VALUE);
  const [model, setModel] = useState(DEFAULT_VALUE);
  const [version, setVersion] = useState(DEFAULT_VALUE);
  const [yearList, setYearList] = useState(() => {
    const currentYear = new Date().getFullYear();

    return help.getYearList(carInstance.isNew ? currentYear - 2 : 1990);
  });
  const [yearId, setYearId] = useState(help.findYearId(yearList, carInstance.year));
  const [selectedPrice, setSelectedPrice] = useState('');
  const [offerPrice, setOfferPrice] = useState('');

  const vehicleStatus = useMemo(
    () => {
      const { isNew } = carInstance;

      if (isNew) {
        return 'new';
      }

      return 'old';
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [carInstance.isNew]
  );
  const hidden = useMemo(
    () => {
      return help.getHiddenFields({
        carInstance,
        hiddenFields,
        isInCredit,
        onClickFullCalc,
        showRestrictions,
        version,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      hiddenFields,
      carInstance.discountType,
      carInstance.isNew,
      version.discount,
      version.discountDirect,
      onClickFullCalc,
      showRestrictions,
      isInCredit,
    ]
  );
  const disableFields = useMemo(() => {
    if (!Array.isArray(disableFieldsProp)) {
      return {};
    }

    return disableFieldsProp.reduce((acc, field) => {
      acc[field] = true;

      return acc;
    }, {});
  }, [disableFieldsProp]);
  const required = useMemo(() => {
    if (!Array.isArray(requiredFields)) {
      return {};
    }

    return requiredFields.reduce((acc, field) => {
      acc[field] = true;

      return acc;
    }, {});
  }, [requiredFields]);

  const wrappedSetState = useCallback(
    ({ field, value }) => {
      let comparedState;
      let setState;

      if (field === FIELDS_NAME.BRAND) {
        comparedState = brand;
        setState = setBrand;
      } else if (field === FIELDS_NAME.MODEL) {
        comparedState = model;
        setState = setModel;
      } else if (field === FIELDS_NAME.VERSION) {
        comparedState = version;
        setState = setVersion;
      }

      if (!comparedState) {
        return false;
      }

      const isEqual = help.shallowEqualObjects(value, comparedState);

      if (!patchIsNew && !isEqual) {
        setState(value);

        return true;
      }

      return false;
    },
    [brand, model, version, patchIsNew]
  );

  const onChangeBrand = useCallback(
    ({ [FIELDS_NAME.BRAND]: { id, value } }) => {
      const changed = wrappedSetState({ field: FIELDS_NAME.BRAND, value: { id, value } });

      if (!changed) {
        return;
      }

      wrappedSetState({ field: FIELDS_NAME.MODEL, value: { ...DEFAULT_VALUE } });
      wrappedSetState({ field: FIELDS_NAME.VERSION, value: { ...DEFAULT_VALUE } });

      const price = Number(selectedPrice);
      const newCarInstance = {
        ...carInstance,
        brand: help.getListValueById(brands, id) || value,
        defaultPrice: 0,
        discountType: '',
        model: '',
        offerPrice: price,
        price,
        version: '',
      };

      onPatch(newCarInstance, true, ['model', 'version'], { brandId: id, vehicleStatus });
      onValidate({
        version: newCarInstance.version,
        brand: newCarInstance.brand,
        model: newCarInstance.model,
        price: newCarInstance.price,
      });
    },
    [wrappedSetState, selectedPrice, carInstance, brands, onPatch, vehicleStatus, onValidate]
  );

  const onChangeModel = useCallback(
    ({ [FIELDS_NAME.MODEL]: { id, value } }) => {
      const changed = wrappedSetState({ field: FIELDS_NAME.MODEL, value: { id, value } });

      if (!changed) {
        return;
      }

      onChangeCarModel();
      wrappedSetState({ field: FIELDS_NAME.VERSION, value: { ...DEFAULT_VALUE } });

      const price = Number(selectedPrice);
      const newCarInstance = {
        ...carInstance,
        defaultPrice: 0,
        discountType: '',
        model: help.getListValueById(models, id) || value,
        offerPrice: price,
        price,
        version: '',
      };

      onPatch(newCarInstance, true, ['version'], { brandId: brand.id, modelId: id, vehicleStatus });
      onValidate({
        version: newCarInstance.version,
        brand: newCarInstance.brand,
        model: newCarInstance.model,
        price: newCarInstance.price,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      wrappedSetState,
      onChangeCarModel,
      selectedPrice,
      carInstance,
      models,
      onPatch,
      brand.id,
      vehicleStatus,
      onValidate,
    ]
  );

  const onChangeVersion = useCallback(
    ({ [FIELDS_NAME.VERSION]: payload }) => {
      const {
        enginePower: versionEnginePower,
        id: versionId,
        price: versionPrice,
        value: versionValue,
      } = payload;

      const version = help.getListValueById(versions, versionId) || versionValue;
      const enginePower = versionEnginePower || '';
      const price = versionPrice || 0;
      const newCarInstance = {
        ...carInstance,
        defaultPrice: price,
        discountType: '',
        enginePower,
        offerPrice: price,
        price,
        version,
      };

      setSelectedPrice(String(price));
      setOfferPrice(String(price));

      onSetCarInstance({ ...carInstance, enginePower });
      onPatch(newCarInstance);
      onValidate({
        version: newCarInstance.version,
        brand: newCarInstance.brand,
        model: newCarInstance.model,
        price: newCarInstance.price,
      });
    },
    [versions, carInstance, onSetCarInstance, onPatch, onValidate]
  );

  const onChangeYear = useCallback(
    ({ [FIELDS_NAME.YEAR]: year }) => {
      setYearId(year);
      onSetYear({ year });
      onChange('year');
    },
    [onSetYear, onChange]
  );

  const onBlurPrice = useCallback(
    ({ [FIELDS_NAME.PRICE]: priceRaw }) => {
      const { discountType } = carInstance;

      const price = Number(onlyNumber(priceRaw) || 0) || 0;
      const discount = help.getDiscount(discountType, version);

      onBlur({ offerPrice: help.getOfferPrice(price, discount, discount > 0), price });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [carInstance.discountType, version, onBlur]
  );

  const onChangePrice = useCallback(
    ({ [FIELDS_NAME.PRICE]: priceRaw }) => {
      const { discountType } = carInstance;

      const price = Number(onlyNumber(priceRaw) || 0) || 0;
      const discount = help.getDiscount(discountType, version);
      const offerPrice = help.getOfferPrice(price, discount, discount > 0);

      setSelectedPrice(priceRaw);
      setOfferPrice(offerPrice);
      onChange('price');
      onValidate({ price, offerPrice });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [carInstance.discountType, version, onChange, onValidate]
  );

  const onBlurMileage = useCallback(
    ({ [FIELDS_NAME.MILEAGE]: mileage }) => {
      onBlur({ mileage: Number(onlyNumber(mileage) || 0) || null });
    },
    [onBlur]
  );

  const onChangeMileage = useCallback(
    ({ [FIELDS_NAME.MILEAGE]: mileage }) => {
      setMileage(mileage);
      onChange('mileage');
    },
    [setMileage, onChange]
  );

  const onChangeIsSetDiscountMfr = useCallback(
    ({ [FIELDS_NAME.IS_SET_DISCOUNT_MFR]: checked }) => {
      const { discount: versionDiscount } = version ?? {};

      const discountType = checked ? DISCOUNTS.mfr : '';
      const offerPrice = help.getOfferPrice(selectedPrice, versionDiscount, checked);

      setOfferPrice(String(offerPrice));
      onPatch({ ...carInstance, discountType, offerPrice });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [version.discount, selectedPrice, onPatch, carInstance]
  );

  const onChangeIsSetDiscountDirect = useCallback(
    ({ [FIELDS_NAME.IS_SET_DISCOUNT_DIRECT]: checked }) => {
      const { discountDirect: versionDiscountDirect } = version ?? {};

      const discountType = checked ? DISCOUNTS.direct : '';
      const offerPrice = help.getOfferPrice(selectedPrice, versionDiscountDirect, checked);

      setOfferPrice(String(offerPrice));
      onPatch({ ...carInstance, discountType, offerPrice });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [version.discountDirect, selectedPrice, onPatch, carInstance]
  );

  useEffect(
    () => {
      if (!model.value) {
        return;
      }

      const image = models.find((item) => {
        return item.value === model.value;
      });

      setImage(image || {});
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [model.value]
  );

  useEffect(
    () => {
      const { id, isNew } = carInstance;

      if (id) {
        const currentYear = new Date().getFullYear();
        const year = help.getYearList(isNew ? currentYear - 2 : 1990);

        setYearList(year);
      }

      // TODO: Меняем локальное состояние но не в сторе ( это не новый код, так было )
      [FIELDS_NAME.BRAND, FIELDS_NAME.MODEL, FIELDS_NAME.VERSION].forEach((field) => {
        wrappedSetState({ field, value: { ...DEFAULT_VALUE } });
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [carInstance.id, carInstance.isNew]
  );

  useEffect(
    () => {
      const { brand } = carInstance;

      const { id, value } = help.getItemByValue(brands, brand);

      wrappedSetState({
        field: FIELDS_NAME.BRAND,
        value: { disabled: false, id, loading: false, value: value || null },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [carInstance.brand, patchIsNew]
  );

  useEffect(
    () => {
      const { model } = carInstance;

      const { id, value } = help.getItemByValue(models, model);

      wrappedSetState({
        field: FIELDS_NAME.MODEL,
        value: { disabled: false, id, loading: false, value: value || null },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [carInstance.model, models, patchIsNew]
  );

  useEffect(
    () => {
      const { version } = carInstance;

      const { discount, discountDirect, id, value } = help.getItemByValue(versions, version);

      wrappedSetState({
        field: FIELDS_NAME.VERSION,
        value: {
          disabled: false,
          discount: Number(discount || 0),
          discountDirect: Number(discountDirect || 0),
          id,
          loading: false,
          value: value || null,
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [carInstance.model, carInstance.version, versions, patchIsNew]
  );

  useEffect(
    () => {
      const { year } = carInstance;

      setYearId(help.findYearId(yearList, year));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [carInstance.year, yearList]
  );

  useEffect(
    () => {
      const { offerPrice: carInstanceOfferPrice, price: carInstancePrice } = carInstance;

      const offerPrice = Number(carInstanceOfferPrice || 0);
      const price = Number(carInstancePrice || 0);

      setOfferPrice(String(offerPrice || price || ''));
      setSelectedPrice(String(price || ''));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [carInstance.offerPrice, carInstance.price]
  );

  useEffect(
    () => {
      if (ref && ref.current && scrollTo === BLOCK_MAIN.name) {
        animateScrollTo(ref.current);
        onScroll();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scrollTo]
  );

  useEffect(
    () => {
      onResetErrors([
        FIELDS_NAME.BRAND,
        FIELDS_NAME.MODEL,
        FIELDS_NAME.VERSION,
        FIELDS_NAME.YEAR,
        FIELDS_NAME.PRICE,
        FIELDS_NAME.OFFER_PRICE,
      ]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [path]
  );

  useEffect(
    () => {
      onClickIsInCredit({ isInCredit });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const fetchBrands = carInfoFetch || allFetch || brandsFetch;
  const fetchModels = fetchBrands || modelsFetch;
  const fetchVersions = fetchModels || versionsFetch;

  return {
    brand,
    disabled: {
      ...DEFAULT_DISABLED,
      [FIELDS_NAME.BRAND]: fetchBrands || disabledProp,
      [FIELDS_NAME.MODEL]: fetchModels || !brand.value || disabledProp,
      [FIELDS_NAME.VERSION]:
        fetchVersions || !model.value || disabledProp || disableFields[FIELDS_NAME.VERSION],
      [FIELDS_NAME.YEAR]: disabledProp,
      [FIELDS_NAME.PRICE]: disabledProp,
      [FIELDS_NAME.MILEAGE]: disabledProp,
      [FIELDS_NAME.IS_SET_DISCOUNT_MFR]: disabledProp,
      [FIELDS_NAME.IS_SET_DISCOUNT_DIRECT]: disabledProp,
      [FIELDS_NAME.IS_IN_CREDIT]: disabledProp,
      [FIELDS_NAME.BANK]: disabledProp,
    },
    fetchBrands,
    fetchModels,
    fetchVersions,
    hidden,
    model,
    moreRow:
      onClickFullCalc ||
      showRestrictions ||
      !hidden[FIELDS_NAME.IS_SET_DISCOUNT_MFR] ||
      !hidden[FIELDS_NAME.IS_SET_DISCOUNT_DIRECT],
    offerPrice,
    onBlurMileage,
    onBlurPrice,
    onChangeBrand,
    onChangeIsSetDiscountDirect,
    onChangeIsSetDiscountMfr,
    onChangeMileage,
    onChangeModel,
    onChangePrice,
    onChangeVersion,
    onChangeYear,
    ref,
    required,
    selectedPrice,
    version,
    yearId,
    yearList,
  };
};
